import { useCallback, useEffect, useRef, useState } from "react";

const MODULE_NAMES = { leads: "Leads", kanban: "Clientes", prospeccao: "Prospecção" };

export function isContactSearchable(value) {
  const text = String(value || "").trim();
  const letters = (text.match(/\p{L}/gu) || []).length;
  const digits = (text.match(/\d/g) || []).length;
  return letters >= 3 || digits === 4 || digits >= 10;
}

function formatUpdatedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return `Índice atualizado ${new Intl.DateTimeFormat("pt-BR", {
    dateStyle: "short", timeStyle: "short",
  }).format(date)}`;
}

export default function GlobalContactSearch({ currentUser, onOpenResult }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [metadata, setMetadata] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [openingId, setOpeningId] = useState(null);
  const controllerRef = useRef(null);
  const timerRef = useRef(null);
  const sequenceRef = useRef(0);
  const identity = `${currentUser?.id ?? ""}:${currentUser?.storeId ?? ""}:${currentUser?.role ?? ""}`;

  const search = useCallback(async (rawQuery, userIdentity) => {
    const value = rawQuery.trim();
    if (!isContactSearchable(value)) return;
    controllerRef.current?.abort();
    const controller = new AbortController();
    controllerRef.current = controller;
    const sequence = ++sequenceRef.current;
    setLoading(true);
    setError("");
    setHasSearched(true);
    try {
      const [scopeUserId, , scopeRole] = identity.split(":");
      const scope = scopeRole === "consultant" ? `&consultantId=${encodeURIComponent(scopeUserId)}` : "";
      const response = await fetch(`/api/crm/contact-search?q=${encodeURIComponent(value)}${scope}`, {
        credentials: "include",
        signal: controller.signal,
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload.error || payload.message || `A busca falhou (HTTP ${response.status}).`);
      }
      if (controller.signal.aborted || sequence !== sequenceRef.current || userIdentity !== identity) return;
      setResults(Array.isArray(payload.results) ? payload.results : []);
      setMetadata({
        generatedAt: payload.generatedAt,
        stale: Boolean(payload.stale),
        truncated: Boolean(payload.truncated),
      });
    } catch (cause) {
      if (cause.name === "AbortError" || controller.signal.aborted || sequence !== sequenceRef.current) return;
      setResults([]);
      setMetadata(null);
      setError(cause.message || "Não foi possível pesquisar contatos.");
    } finally {
      if (!controller.signal.aborted && sequence === sequenceRef.current) setLoading(false);
    }
  }, [identity]);

  useEffect(() => {
    controllerRef.current?.abort();
    sequenceRef.current += 1;
    setResults([]);
    setMetadata(null);
    setError("");
    setHasSearched(false);
    setLoading(false);
    if (!isContactSearchable(query)) return undefined;
    const timer = window.setTimeout(() => search(query, identity), 300);
    timerRef.current = timer;
    return () => {
      window.clearTimeout(timer);
      controllerRef.current?.abort();
    };
  }, [query, identity, search]);

  const submit = event => {
    event.preventDefault();
    window.clearTimeout(timerRef.current);
    if (!isContactSearchable(query)) {
      setError("Digite pelo menos 3 letras, os 4 últimos dígitos ou um telefone completo.");
      return;
    }
    void search(query, identity);
  };

  const changeQuery = event => {
    setQuery(event.target.value);
    setError("");
  };

  const openResult = async result => {
    if (openingId !== null) return;
    const sequence = sequenceRef.current;
    setOpeningId(result.id);
    setError("");
    try {
      await onOpenResult?.(result);
      if (sequence === sequenceRef.current) {
        setQuery(""); setResults([]); setMetadata(null); setHasSearched(false);
      }
    } catch (cause) {
      if (sequence === sequenceRef.current) setError(cause.message || "Não foi possível abrir este cartão.");
    } finally {
      setOpeningId(null);
    }
  };

  return (
    <div style={{ position: "relative", width: "min(430px, 38vw)", minWidth: 220, flex: "0 1 430px", zIndex: 60 }}>
      <form onSubmit={submit} role="search" aria-label="Buscar contatos em todos os módulos"
        style={{ display: "flex", alignItems: "center", gap: 7, padding: "5px 7px 5px 12px", background: "rgba(255,255,255,.96)", border: "1px solid rgba(24,48,55,.18)", borderRadius: 9, boxShadow: "0 3px 12px rgba(20,36,40,.12)" }}>
        <span aria-hidden="true" style={{ color: "#557075", fontSize: 17, lineHeight: 1 }}>⌕</span>
        <input
          value={query}
          onChange={changeQuery}
          onKeyDown={event => { if (event.key === "Escape") { setQuery(""); setResults([]); setHasSearched(false); } }}
          placeholder="Buscar nome ou telefone"
          aria-label="Nome ou telefone"
          aria-describedby="global-contact-search-hint"
          style={{ minWidth: 0, flex: 1, padding: "6px 0", border: 0, outline: 0, background: "transparent", color: "#23383B", fontSize: 13, fontFamily: "inherit" }}
        />
        {loading && <span aria-label="Pesquisando" style={{ width: 13, height: 13, border: "2px solid #C5D1CD", borderTopColor: "#176B66", borderRadius: "50%", animation: "gcs-spin .7s linear infinite" }} />}
        {query && <button type="button" onClick={() => { setQuery(""); setResults([]); setMetadata(null); setError(""); setHasSearched(false); }}
          aria-label="Limpar busca" style={{ border: 0, background: "transparent", color: "#557075", fontSize: 18, cursor: "pointer", padding: "0 3px" }}>×</button>}
        <button type="submit" style={{ border: 0, borderRadius: 6, padding: "7px 10px", color: "#fff", background: "#176B66", fontWeight: 700, fontSize: 12, cursor: "pointer", whiteSpace: "nowrap" }}>Buscar</button>
      </form>
      <div id="global-contact-search-hint" style={{ position: "absolute", width: 1, height: 1, overflow: "hidden", clip: "rect(0,0,0,0)" }}>
        Digite pelo menos 3 letras, os 4 últimos dígitos ou um telefone completo.
      </div>
      {(query.trim() || error) && (
        <div style={{ position: "absolute", top: "calc(100% + 7px)", left: 0, right: 0, maxHeight: "min(65vh, 520px)", overflowY: "auto", background: "#F8FAF7", border: "1px solid #D8E2DC", borderRadius: 11, boxShadow: "0 14px 34px rgba(22,45,47,.20)", color: "#203638" }}>
          {error && <div role="alert" style={{ padding: "12px 14px", color: "#8A3328", fontSize: 13, lineHeight: 1.45, background: "#FFF3EF", borderBottom: "1px solid #EBD5CF" }}>
            <strong>Não foi possível concluir.</strong> {error}
            {/index|preparad|build|constru/i.test(error) && <div style={{ marginTop: 4 }}>O índice de contatos ainda não está preparado. Tente novamente mais tarde.</div>}
          </div>}
          {!isContactSearchable(query) && !error && <div style={{ padding: "12px 14px", fontSize: 12, color: "#607579" }}>Digite pelo menos 3 letras, os 4 últimos dígitos ou um telefone completo.</div>}
          {loading && <div role="status" style={{ padding: "12px 14px", fontSize: 12, color: "#607579" }}>Pesquisando nos módulos…</div>}
          {!loading && !error && hasSearched && results.length === 0 && <div style={{ padding: "14px", fontSize: 13, color: "#4C6264" }}>Nenhum contato encontrado para esta busca.</div>}
          {metadata && (
            <div style={{ padding: "8px 12px", borderBottom: results.length ? "1px solid #DDE5DF" : 0, fontSize: 10, color: "#667A78", display: "flex", flexWrap: "wrap", gap: "4px 10px" }}>
              {metadata.generatedAt ? <span>{formatUpdatedAt(metadata.generatedAt)}</span> : <span>Horário de atualização indisponível</span>}
              {metadata.stale && <strong style={{ color: "#906116" }}>Índice desatualizado; os resultados podem estar incompletos.</strong>}
              {metadata.truncated && <strong style={{ color: "#906116" }}>Há mais resultados. Refine a busca ou informe o telefone completo.</strong>}
            </div>
          )}
          {results.map(result => {
            const legacy = Boolean(result.legacy);
            const title = `${result.name || "Sem nome"}${result.phone ? ` · ${result.phone}` : ""}`;
            return (
              <div key={`${result.module}:${result.id}:${result.storeId}`} style={{ padding: "10px 13px", borderBottom: "1px solid #E4EAE5", display: "flex", alignItems: "center", gap: 10 }}>
                <div aria-hidden="true" style={{ width: 32, height: 32, flex: "0 0 32px", display: "grid", placeItems: "center", borderRadius: 9, background: legacy ? "#E8E5DD" : "#DCECE5", color: legacy ? "#766B56" : "#176B66", fontSize: 11, fontWeight: 800 }}>{(result.name || "?").trim().slice(0, 1).toLocaleUpperCase()}</div>
                <div style={{ minWidth: 0, flex: 1 }}>
                  <div title={title} style={{ fontWeight: 700, fontSize: 13, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{result.name || "Sem nome"}</div>
                  <div style={{ fontSize: 11, color: "#637779", marginTop: 2 }}>{result.phone || "Telefone não informado"} · {MODULE_NAMES[result.module] || result.module}</div>
                  {legacy && <div style={{ fontSize: 10, color: "#81765F", marginTop: 3 }}>Registro histórico · somente leitura</div>}
                </div>
                {legacy
                  ? <span style={{ fontSize: 10, color: "#81765F", whiteSpace: "nowrap" }}>Não disponível</span>
                  : <button type="button" disabled={openingId !== null} onClick={() => { void openResult(result); }} aria-label={`Abrir ${result.name || result.phone}`}
                    style={{ border: "1px solid #B7D2C7", borderRadius: 6, padding: "6px 9px", background: "#F1F8F3", color: "#176B66", fontWeight: 700, fontSize: 11, cursor: "pointer", whiteSpace: "nowrap" }}>{openingId === result.id ? "Abrindo…" : "Abrir"}</button>}
              </div>
            );
          })}
        </div>
      )}
      <style>{`@keyframes gcs-spin { to { transform: rotate(360deg); } } @media (max-width: 720px) { .gcs-search { width: 100%; } }`}</style>
    </div>
  );
}
